import { useEffect, useState, type FormEvent } from "react";

import { apiClient, type GovernanceHealth, type UsageReport } from "../../api/client";
import { errorMessage } from "./errors";

export function UsageHealth() {
  const [health, setHealth] = useState<GovernanceHealth | null>(null);
  const [usage, setUsage] = useState<UsageReport | null>(null);
  const [providerId, setProviderId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [userId, setUserId] = useState("");
  const [skill, setSkill] = useState("");
  const [message, setMessage] = useState("");

  async function loadUsage(event?: FormEvent) {
    event?.preventDefault();
    try {
      setUsage(
        await apiClient.getUsageReport({
          provider_id: providerId.trim() || undefined,
          department_id: departmentId.trim() || undefined,
          user_id: userId.trim() || undefined,
          skill_identifier: skill.trim() || undefined,
        }),
      );
      setMessage("");
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  async function loadHealth() {
    try {
      setHealth(await apiClient.getGovernanceHealth());
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }

  useEffect(() => {
    void Promise.all([loadUsage(), loadHealth()]);
  }, []);

  return (
    <section className="flow-panel" id="usage-health">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">owner · 最近 30 天</span>
          <h2>用量、成本与治理健康</h2>
        </div>
        <button className="secondary-button" onClick={() => void loadHealth()} type="button">
          刷新健康
        </button>
      </div>
      {message && (
        <p className="alert error" role="alert">
          {message}
        </p>
      )}
      {health && (
        <div className="health-grid">
          <HealthItem label="总状态" value={health.status} warning={health.status !== "ok"} />
          <HealthItem label="数据库" value={health.database} />
          <HealthItem
            label="审计链"
            value={health.audit_chain_valid ? "有效" : "异常"}
            warning={!health.audit_chain_valid}
          />
          <HealthItem label="审计事件" value={String(health.audit_events)} />
          <HealthItem label="活动供应商" value={String(health.providers.active ?? 0)} />
          <HealthItem
            label="队列深度"
            value={String(health.operational.queue.redis_depth ?? "不可用")}
            warning={health.operational.services.queue !== "ok"}
          />
          <HealthItem
            label="对象存储"
            value={health.operational.services.storage ?? "未知"}
            warning={health.operational.services.storage !== "ok"}
          />
          <HealthItem
            label="供应商平均延迟"
            value={metric(health.operational.latency.provider_average_ms_24h, " ms")}
          />
          <HealthItem
            label="运行并发"
            value={String(health.operational.concurrency.running_runs ?? 0)}
            warning={
              (health.operational.concurrency.running_runs ?? 0) >=
              (health.operational.concurrency.warning_threshold ?? 10)
            }
          />
          <HealthItem
            label="失败调用"
            value={String(health.usage.failed ?? 0)}
            warning={(health.usage.failed ?? 0) > 0}
          />
          <HealthItem
            label="24h 成本"
            value={String(health.operational.usage.cost_24h ?? "0")}
          />
        </div>
      )}
      {health && health.operational.alerts.length > 0 && (
        <p className="alert warning" role="status">
          运维告警：{health.operational.alerts.join("、")}
        </p>
      )}
      <form className="usage-filter" onSubmit={(event) => void loadUsage(event)}>
        <label>
          供应商 ID
          <input onChange={(event) => setProviderId(event.target.value)} value={providerId} />
        </label>
        <label>
          部门 ID
          <input onChange={(event) => setDepartmentId(event.target.value)} value={departmentId} />
        </label>
        <label>
          用户 ID
          <input onChange={(event) => setUserId(event.target.value)} value={userId} />
        </label>
        <label>
          Skill 标识
          <input onChange={(event) => setSkill(event.target.value)} value={skill} />
        </label>
        <button type="submit">重新汇总</button>
      </form>
      {usage && (
        <dl className="usage-metrics">
          <div>
            <dt>调用尝试</dt>
            <dd>{usage.attempts}</dd>
          </div>
          <div>
            <dt>输入 Token</dt>
            <dd>{usage.input_tokens}</dd>
          </div>
          <div>
            <dt>输出 Token</dt>
            <dd>{usage.output_tokens}</dd>
          </div>
          <div>
            <dt>缓存 Token</dt>
            <dd>{usage.cached_tokens}</dd>
          </div>
          <div>
            <dt>图片单位</dt>
            <dd>{usage.image_units}</dd>
          </div>
          <div>
            <dt>最终成本</dt>
            <dd>{usage.total_cost}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}

function metric(value: number | null | undefined, suffix: string) {
  return value == null ? "暂无数据" : `${value}${suffix}`;
}

function HealthItem({
  label,
  value,
  warning = false,
}: {
  label: string;
  value: string;
  warning?: boolean;
}) {
  return (
    <div className={warning ? "warning" : ""}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
